import { Fragment } from 'react';
import { FovTypography } from 'components';
import { FovPopover } from 'components/generic';
import { DownloadOutlined, EyeOutlined } from 'components/icons';
import {
    DateTimeContainer,
    StatusBadge,
    JobId,
    TruncatedCell,
    DurationCell,
    RowMenuButton,
    RowMenuItem,
} from './styles';

const { VITE_CDN_HOST: cdnHost } = process.env;

const jobDownloadUrl = job => (
    job.status?.toLowerCase() === 'failed'
        ? `${cdnHost}/public/failed-jobs/${job.job_guid}.zip`
        : null
);

const jobPreviewUrl = job => (
    job.rpc_guid ? `https://preview.archvision.app/fovea/${job.rpc_guid}` : null
);

export const listColumns = (selectedTheme, openMenuGuid, setOpenMenuGuid) => ([
    {
        title: 'Job ID',
        key: 'job_guid',
        width: 320,
        alignContent: 'left',
        render: (text, job) => (
            <JobId title={job.job_guid}>
                {job.job_guid ?? '-'}
            </JobId>
        ),
    },
    {
        title: 'File Name',
        key: 'filename',
        width: 240,
        alignContent: 'left',
        render: (text, job) => (
            <TruncatedCell title={job.filename}>
                {job.filename ?? '-'}
            </TruncatedCell>
        ),
    },
    {
        title: 'Job Type',
        key: 'type',
        width: 185,
        alignContent: 'left',
        render: (text, job) => (
            <TruncatedCell title={job.type}>
                {job.type ?? '-'}
            </TruncatedCell>
        ),
    },
    {
        title: 'Email',
        key: 'notification_email',
        width: 240,
        alignContent: 'left',
        render: (text, job) => (
            <TruncatedCell title={job.notification_email}>
                {job.notification_email ?? '-'}
            </TruncatedCell>
        ),
    },
    {
        title: 'Application',
        key: 'application',
        width: 140,
        alignContent: 'left',
        render: (text, job) => (
            <TruncatedCell title={job.application}>
                {job.application ?? '-'}
            </TruncatedCell>
        ),
    },
    {
        title: 'Status',
        key: 'status',
        width: 145,
        alignContent: 'left',
        render: (text, job) => (
            <StatusBadge
                $status={job.status}
                $selectedTheme={selectedTheme}
            >
                {job.status
                    ?.replaceAll('_', ' ')
                    .replace(/\b\w/g, letter => letter.toUpperCase())}
            </StatusBadge>
        ),
    },
    {
        title: 'Created ↓',
        key: 'created_at',
        width: 140,
        render: (text, job) => (
            <DateTimeContainer>
                <FovTypography>
                    {job.created_at ? new Date(job.created_at).toLocaleDateString(
                        'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                </FovTypography>

                <FovTypography>
                    {job.created_at ? new Date(job.created_at).toLocaleTimeString(
                        'en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) : '-'}
                </FovTypography>
            </DateTimeContainer>
        ),
    },
    {
        title: 'Duration',
        key: 'duration',
        width: 100,
        render: (text, job) => {
            if (
                ![ 'completed', 'failed' ].includes(job.status?.toLowerCase()) ||
                !job.created_at ||
                !job.updated_at) {
                return <FovTypography>-</FovTypography>;
            }

            const createdAt = new Date(job.created_at);
            const completedAt = new Date(job.updated_at);
            const durationMs = completedAt.getTime() - createdAt.getTime();

            if (
                Number.isNaN(createdAt.getTime()) ||
                Number.isNaN(completedAt.getTime()) ||
                durationMs < 0
            ) {
                return <DurationCell>-</DurationCell>;
            }

            const totalSeconds = Math.floor(durationMs / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            const duration = [
                hours,
                minutes,
                seconds,
            ]
                .map(value => String(value).padStart(2, '0'))
                .join(':');

            return <DurationCell>{duration}</DurationCell>;
        },
    },
    {
        title: '',
        key: 'actions',
        width: 60,
        alignContent: 'center',
        render: (text, job) => {
            const downloadUrl = jobDownloadUrl(job);
            const previewUrl = jobPreviewUrl(job);

            return (
                <FovPopover
                    noArrow
                    position='bottom'
                    top={32}
                    right={8}
                    padding={4}
                    isVisible={openMenuGuid === job.job_guid}
                    callback={() => setOpenMenuGuid(
                        current => (current === job.job_guid ? null : job.job_guid),
                    )}
                    content={(
                        <Fragment>
                            <RowMenuItem
                                download
                                href={downloadUrl ?? undefined}
                                $disabled={!downloadUrl}
                                $selectedTheme={selectedTheme}
                                title={downloadUrl ? undefined : 'Only failed jobs can be downloaded'}
                            >
                                <DownloadOutlined width={16} height={16} />
                                Download
                            </RowMenuItem>

                            <RowMenuItem
                                target='_blank'
                                rel='noreferrer'
                                href={previewUrl ?? undefined}
                                $disabled={!previewUrl}
                                $selectedTheme={selectedTheme}
                                title={previewUrl ? undefined : 'This job has no model to preview'}
                            >
                                <EyeOutlined width={16} height={16} />
                                Preview
                            </RowMenuItem>
                        </Fragment>
                    )}
                >
                    <RowMenuButton type='button' $selectedTheme={selectedTheme}>
                        <span aria-hidden='true'>⋮</span>
                    </RowMenuButton>
                </FovPopover>
            );
        },
    },
]);